import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Eraser, Trash2, Pen, Download, MessageSquarePlus, Undo2 } from "lucide-react";

const COLORS = ["#0A192F", "#57C5C6", "#F77FBE", "#152E52"];

export function Whiteboard({ onAttach }) {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const history = useRef([]);          // stack of pre-change canvas snapshots
  const [color, setColor] = useState("#0A192F");
  const [erase, setErase] = useState(false);
  const [size, setSize] = useState(3);
  const [canUndo, setCanUndo] = useState(false);

  const snapshot = () => {
    try {
      const c = canvasRef.current;
      history.current.push(c.toDataURL());
      if (history.current.length > 40) history.current.shift();
      setCanUndo(true);
    } catch { /* tainted canvas guard */ }
  };

  const undo = () => {
    const prev = history.current.pop();
    setCanUndo(history.current.length > 0);
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    if (prev === undefined) return;
    const img = new Image();
    img.onload = () => { ctx.clearRect(0, 0, c.width, c.height); ctx.drawImage(img, 0, 0); };
    img.src = prev;
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    const resize = () => {
      const prev = canvas.toDataURL();
      const rect = wrap.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = rect.height;
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0);
      img.src = prev;
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };

  const start = (e) => {
    drawing.current = true;
    snapshot();            // cache state before each stroke for per-stroke undo
    last.current = pos(e);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.globalCompositeOperation = erase ? "destination-out" : "source-over";
    ctx.strokeStyle = color;
    ctx.lineWidth = erase ? 18 : size;
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
  };
  const end = () => { drawing.current = false; };

  const clear = () => {
    const c = canvasRef.current;
    snapshot();            // cache the full drawing so Undo can recover a wipe
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
  };

  const exportPng = () => {
    const src = canvasRef.current;
    const out = document.createElement("canvas");
    out.width = src.width;
    out.height = src.height;
    const ctx = out.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, out.width, out.height);
    ctx.drawImage(src, 0, 0);
    return out.toDataURL("image/png");
  };

  const download = () => {
    const a = document.createElement("a");
    a.href = exportPng();
    a.download = `occ-whiteboard-${Date.now()}.png`;
    a.click();
  };

  const attach = () => { if (onAttach) onAttach(exportPng()); };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-1.5">
          {COLORS.map((c) => (
            <button key={c} onClick={() => { setColor(c); setErase(false); }}
              data-testid={`wb-color-${c}`}
              className={`h-6 w-6 rounded-full border-2 transition-transform ${
                color === c && !erase ? "scale-110 border-white ring-2 ring-persian-blue" : "border-white/30"
              }`} style={{ background: c }} />
          ))}
        </div>
        <div className="flex items-center gap-1.5 ml-1">
          {[2, 4, 7].map((s) => (
            <button key={s} onClick={() => { setSize(s); setErase(false); }} data-testid={`wb-size-${s}`}
              className={`h-7 w-7 rounded-md flex items-center justify-center ${
                size === s && !erase ? "bg-persian-blue/30 ring-1 ring-persian-blue" : "bg-white/5"
              }`}>
              <span className="rounded-full bg-white" style={{ width: s + 2, height: s + 2 }} />
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1.5 ml-auto">
          {onAttach && (
            <Button size="sm" variant="ghost" onClick={attach} data-testid="wb-attach"
              className="h-8 text-persian-blue hover:text-persian-bluehover">
              <MessageSquarePlus className="h-4 w-4" />
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={undo} disabled={!canUndo} data-testid="wb-undo"
            className="h-8 text-slate-300 hover:text-white disabled:opacity-30">
            <Undo2 className="h-4 w-4" />
          </Button>
          <Button size="sm" variant="ghost" onClick={download} data-testid="wb-download"
            className="h-8 text-slate-300 hover:text-white">
            <Download className="h-4 w-4" />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setErase((v) => !v)} data-testid="wb-eraser"
            className={`h-8 ${erase ? "bg-persian-rose/20 text-persian-rose" : "text-slate-300"} hover:text-white`}>
            {erase ? <Pen className="h-4 w-4" /> : <Eraser className="h-4 w-4" />}
          </Button>
          <Button size="sm" variant="ghost" onClick={clear} data-testid="wb-clear"
            className="h-8 text-slate-300 hover:text-persian-rose">
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <div ref={wrapRef} className="flex-1 rounded-xl bg-white overflow-hidden touch-none" style={{ minHeight: "340px" }}>
        <canvas ref={canvasRef} data-testid="whiteboard-canvas"
          className="w-full h-full cursor-crosshair"
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end} />
      </div>
    </div>
  );
}
